import { useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useMediaQuery } from 'react-responsive';
import { useLocale, useTranslations } from 'next-intl';

import { Button } from '@/component/Form/Button';
import { Image } from '@/component/Image';
import { dateLocales } from '@/i18n';
import { getListingLocaleSlug } from '@/utils';
import { pushListingCard, mapGtmItem } from '@/utils/gtm';

import styles from './Card.module.css';
import { ReviewsStars } from '@/component/Reviews/ReviewsStars';
import { Link } from '@/i18n/routing';
import { allClearHtml } from '@/utils/html';
import { ReviewsModal } from '@/component/Reviews/ReviewsModal';
import { CardCarousel } from '../listings/CardCarousel';

export const ReviewsCard = ({
  item,
  dataTestId,
  pageName = 'reviews page',
}) => {
  const refText = useRef();
  const refFullText = useRef();
  const [isMore, setMore] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const locale = useLocale();
  const t = useTranslations();

  const { author, createdAt, text, rating: stars, listing } = item;

  const isMobile = useMediaQuery({ query: '(max-width: 639px)' });

  const pushGTM = () => pushListingCard(pageName, mapGtmItem(item.listing));

  const cardUrl = {
    href: `/listings/${getListingLocaleSlug(item.listing, locale)}`,
    target: isMobile ? '_self' : '_blank',
    onClick: pushGTM,
  };

  const markup = { __html: t.raw(allClearHtml(text)) };

  useEffect(() => {
    if (!isMore) {
      if (refText.current && refFullText.current) {
        const currentTextHeight =
          refText.current.getBoundingClientRect().height;
        const fullTextHeight =
          refFullText.current.getBoundingClientRect().height;
        if (fullTextHeight > currentTextHeight) {
          setMore(true);
        }
      }
    }
  }, [isMore, refText, refFullText]);

  return (
    <div className={styles.inner} data-testid={dataTestId}>
      <div className={styles.card}>
        <div className={styles.card_body}>
          <div className="flex items-center gap-[10px] max-lg:mb-2.5 lg:mb-3">
            <div className="flex items-center gap-[14px]">
              <div className="h-11 w-11 rounded-full overflow-hidden">
                {author.avatar ? (
                  <Image
                    className={styles.avatar}
                    height={44}
                    width={44}
                    alt={author.name}
                    title={author.name}
                    src={author.avatar}
                  />
                ) : (
                  <span
                    className={`rounded-full size-11 inline-flex items-center justify-center bg-slate-800 text-white`}
                  >
                    {author.name[0]}
                  </span>
                )}
              </div>
              <div className="">
                <div className="text-slate-700 text-base font-medium">
                  {author.name}
                </div>
                <div className="space-x-2">
                  <ReviewsStars stars={stars} />
                  <span className="text-slate-500 text-xs">
                    {formatDistanceToNow(new Date(createdAt), {
                      locale: dateLocales[locale],
                      addSuffix: true,
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className={styles.card_body_info}>
              <div>
                <Link
                  {...cardUrl}
                  className="flex flex-col justify-between text-slate-500 text-sm font-semibold underline"
                  prefetch={false}
                >
                  {listing?.name}
                </Link>
              </div>
            </div>

            <div className={styles.card_footer}>
              <div
                ref={refText}
                className="line-clamp-5 text-slate-700 font-normal text-sm mt-4"
                dangerouslySetInnerHTML={markup}
              />
              <div
                ref={refFullText}
                className="absolute -z-10 opacity-0 pointer-events-none text-slate-700 font-normal text-sm mt-4"
                dangerouslySetInnerHTML={markup}
              />
            </div>
          </div>

          <div className="max-md:mt-4 mt-2">
            {isMore && (
              <Button
                className="text-slate-500 cursor-pointer text-sm !px-0 hover:bg-transparent focus:bg-transparent h-auto !font-normal underline leading-tight"
                theme="link"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(true);
                }}
              >
                {t('slider.reviews.full')}
              </Button>
            )}

            {isOpen && (
              <ReviewsModal
                show={isOpen}
                close={() => setIsOpen(false)}
                item={item}
                pageName={pageName}
              />
            )}
          </div>
        </div>
        <CardCarousel
          previewImages={listing?.previewImages}
          cardUrl={cardUrl}
        />
      </div>
    </div>
  );
};
